import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  orderBy, 
  where,
  setDoc 
} from 'firebase/firestore';
import { db } from './config';
import { Apartment, ApartmentStatus } from '../types/Apartment';

// Neue Profilbild-URLs entsprechend der Zuordnung A1=1.jpg, B1=2.jpg, A2=3.jpg, B2=4.jpg etc.
const profileImages = [
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113d850e99612050a40_1.jpg', // A1
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113fe99593508f25f01_2.jpg', // B1
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113be99d7ba07271222_3.jpg', // A2
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113cb56ce5a1ea00a74_4.jpg', // B2
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1127081ec278633b0d1_5.jpg', // A3
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1130842b3ce678a6aff_6.jpg', // B3
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113253078ca0c887b44_7.jpg', // A4
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f113105219c626276dbb_8.jpg', // B4
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f114a76e2babd6225bb5_9.jpg', // A5
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f114826db4c61ccc7757_10.jpg', // B5
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1149297eaa439e39c8b_11.jpg', // A6
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f115173cbb20c2b99df6_12.jpg', // B6
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f115c00d6d6ea04f3625_13.jpg', // A7
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f11545c2037da5040d9e_14.jpg', // B7
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f115173cbb20c2b99dfb_15.jpg', // A8
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1156fdff45c98ce10b5_16.jpg', // B8
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1166014e8629720934c_17.jpg', // A9
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f11624ae8991696c39c4_18.jpg', // B9
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f11628e1e50ce76e51c2_19.jpg', // A10
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f117fe99593508f25f55_20.jpg', // B10
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1174c79d5e6134fcca4_21.jpg', // A11
  'https://cdn.prod.website-files.com/685d2a1749025f45e74d4e97/6886f1137418f1dd3e570f09_22.jpg'  // B11
];

// Funktion um das richtige Profilbild basierend auf der Apartment ID zu erhalten
const getProfileImage = (apartmentId: number): string => {
  // A-Wohnungen (ungerade Indizes): ID 1=Index 0, ID 2=Index 2, ID 3=Index 4, etc.
  // B-Wohnungen (gerade Indizes): ID 12=Index 1, ID 13=Index 3, ID 14=Index 5, etc.
  if (apartmentId <= 11) {
    // Haus A: A1=0, A2=2, A3=4, etc.
    return profileImages[(apartmentId - 1) * 2];
  } else {
    // Haus B: B1=1, B2=3, B3=5, etc.
    return profileImages[((apartmentId - 12) * 2) + 1];
  }
};

// Collection names
export const COLLECTIONS = {
  APARTMENTS: 'apartments',
  USERS: 'users',
  SETTINGS: 'settings'
};

/**
 * Generiert die URLs für die 360°-Bilder der Überbauung.
 * Optimiert für schnellere Ladezeiten: 25 Bilder (jedes vierte: 0001, 0005, 0009, ..., 0097)
 * Mobile (<=850px): building-360-mobile
 * Tablet & Desktop (>850px): building-360
 */
export const generateBuilding360Images = (): string[] => {
  const cloudfrontUrl = 'https://d2tbav89ixiad0.cloudfront.net';
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 850;
  const folder = isMobile ? 'building-360-mobile' : 'building-360';
  
  const images: string[] = [];
  
  for (let i = 1; i <= 100; i += 4) {
    const imageNumber = i.toString().padStart(4, '0');
    images.push(`${cloudfrontUrl}/${folder}/${imageNumber}_ergebnis.webp`);
  }
  
  return images;
};

/**
 * Generiert die URLs für die realen 360°-Bilder der Überbauung.
 * Optimiert für schnellere Ladezeiten: 25 Bilder (jedes vierte Bild)
 * Tablet & Desktop (>850px): Webflow CDN
 * Mobile (≤850px): CloudFront building-360-real-mobile
 */
export const generateBuilding360RealImages = (): string[] => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 850;
  
  // Desktop & Tablet: Use hardcoded Webflow CDN URLs (25 images - every fourth one)
  if (!isMobile) {
    const allImages = [
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68575b3a30385774381_0001_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a685cb104dc51edaea4e_0002_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6853ffc25050adba7b1_0004_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a685b82ece2f5b5dc5fd_0005_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a683750fdefaeaac6ac7_0006_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6834837d9526e2048ca_0007_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6825bac0334ac69a195_0008_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6828a72b8fbae13720a_0009_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a682c1a99349b54e0005_0010_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a682a632f2399795adc6_0012_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a682e00a2ece56394220_0013_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a682a51b001394e22d80_0014_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67faa67b56c8de5f576_0015_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67f7ffb5fdd0a76a133_0016_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67e08528d671065c043_0017_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67ee1097d94d374fba9_0018_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67e3a8e5318f17f1cd4_0019_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67ea76b4b763355bba8_0020_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67e5bac0334ac69a160_0021_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67e21ac2617e78c7249_0022_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67c4837d9526e204859_0023_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67b2de37deef943e170_0024_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67c642f6edab54c26b2_0025_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67ae02d9505304cbf17_0026_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67be7401c95da5a5bb4_0027_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67b44bba020b2c13804_0028_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67b52cb288fe4dba4ef_0029_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67b7e5fc505b9acccef_0030_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a679c634cb1c8767829c_0031_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a678a17bafd64be0e7e1_0032_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a679cdad7f46456c09e2_0033_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a677642f6edab54c2662_0034_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a678cb6fdcb5d968a30a_0035_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67744f231d45ff4cdda_0036_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6772e758df565b8d97d_0037_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a677e02d9505304cbeb1_0038_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a675634217da7e9a9ea2_0039_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a676f7ceea05c50a464e_0040_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6753ffc25050adba680_0041_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6747fd3a46a62656bad_0042_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a674e3fa92788a73f728_0043_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a674d0c5c9d63f1ca6e1_0044_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6735bac0334ac69a0ea_0045_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6730a2302b8610457cb_0046_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67206c5bc0db3c9e14a_0047_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6724cc7bc9a713c820f_0049_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a671c89b1e92a856f841_0050_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a66f14e095ebae4e7b04_0051_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6705a92d121f7acf1f0_0052_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a66f7fd3a46a62656ac7_0053_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a670f323afc09fdec420_0054_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a66ecb6fdcb5d968a273_0055_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a66fe5d67db47785702b_0056_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a67002e392d84b58dd53_0057_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a66f4837d9526e20473a_0058_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a695f7ceea05c50a485d_0059_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6951f4a5827fe5b8718_0060_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a696a196c0f7b82ce37c_0061_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a69598659d321f04d255_0062_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6966a010fef2fe7566f_0063_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a695c4e2c1b29a040152_0064_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a694a51b001394e22f90_0065_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a694b47331a4d7277859_0066_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6927cd6438e3baaf16d_0067_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a69266e3afba3c2d2c53_0068_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a692225ee4460ce957f1_0069_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6928bfb01668207b054_0070_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6925b508b4ffc892708_0071_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a692952b57ece13d5c86_0072_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a69077d4fe4b66175b6d_0073_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6916a010fef2fe755b5_0074_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68f8a72b8fbae1373f8_0075_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68fb877c8b1483ce7b1_0076_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68f3615532e3e916e37_0077_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68f2e00f5dab3109bfe_0078_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68e3f2464c805b1a509_0079_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68fde78471ac90788f7_0080_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68d6f03503263bdbae8_0081_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68d98659d321f04d20f_0082_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68c8ef4cc2a27a4f5ff_0083_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68bf0037622117eb04c_0084_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68c91e37dd6f472bbb5_0085_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68c6f598345e6735079_0086_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68be1d9f98742f8bba8_0087_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68cea0cdc35e2e4f6d0_0088_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68a3a8e5318f17f30b9_0089_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68acb6fdcb5d968a623_0090_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a688a196c0f7b82ce29d_0091_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6888bfb016682079c13_0092_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68914e095ebae4e9005_0093_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6896f03503263bdba4d_0094_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6891f733b8181fb233c_0095_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a689796517992b506156_0096_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a687c89b1e92a856f94d_0097_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a685a46b473c61ee3f83_0098_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a6875a92d121f7acf32c_0099_compressed.jpg',
      'https://cdn.prod.website-files.com/6898cb39dcf5b87c03317f6a/6906a68511e8520ba8fb67a0_0100_compressed.jpg'
    ];
    
    // Return every fourth image (25 images)
    return allImages.filter((_, index) => index % 4 === 0);
  }
  
  // Mobile: Use CloudFront URLs
  const cloudfrontUrl = 'https://d2tbav89ixiad0.cloudfront.net';
  const folder = 'building-360-real-mobile';
  
  const images: string[] = [];
  const excludedImages = [3, 11, 48]; // Diese Bilder fehlen in der realen Serie
  
  for (let i = 1; i <= 100; i++) {
    if (excludedImages.includes(i)) {
      continue;
    }
    
    const imageNumber = i.toString().padStart(4, '0');
    images.push(`${cloudfrontUrl}/${folder}/${imageNumber}_ergebnis.webp`);
  }
  // Return every fourth image (25 images)
  return images.filter((_, index) => index % 4 === 0);
};

/**
 * Mapping-Funktionen für die Synchronisation zwischen Standard- und Real-Bildserien
 * Standard & Real: je 25 Bilder (360° / 25 = 14.4° pro Bild)
 * Standard SVG: 200 Frames (Index 0→Frame 1, Index 1→Frame 9, jedes 8. SVG)
 * Real SVG: 100 Frames (Index 0→Frame 1, Index 1→Frame 5, jedes 4. SVG)
 */

/**
 * Konvertiert einen Standard-Bildindex zu dem entsprechenden Real-Bildindex basierend auf Winkel-Position
 * @param standardIndex - Index im Standard-Bildsatz (0-24)
 * @returns Index im Real-Bildsatz (0-24)
 */
export const convertStandardToRealIndex = (standardIndex: number): number => {
  const standardImageCount = 25;
  const realImageCount = 25;
  
  // Berechne den Winkel der aktuellen Position im Standard-Bildsatz
  const anglePerStandardImage = 360 / standardImageCount;
  const currentAngle = (standardIndex * anglePerStandardImage) % 360;
  
  // Berechne den entsprechenden Index im Real-Bildsatz
  const anglePerRealImage = 360 / realImageCount;
  const realIndex = Math.round(currentAngle / anglePerRealImage) % realImageCount;
  
  return realIndex;
};

/**
 * Konvertiert einen Real-Bildindex zu dem entsprechenden Standard-Bildindex basierend auf Winkel-Position
 * @param realIndex - Index im Real-Bildsatz (0-24)
 * @returns Index im Standard-Bildsatz (0-24)
 */
export const convertRealToStandardIndex = (realIndex: number): number => {
  const standardImageCount = 25;
  const realImageCount = 25;
  
  // Berechne den Winkel der aktuellen Position im Real-Bildsatz
  const anglePerRealImage = 360 / realImageCount;
  const currentAngle = (realIndex * anglePerRealImage) % 360;
  
  // Berechne den entsprechenden Index im Standard-Bildsatz
  const anglePerStandardImage = 360 / standardImageCount;
  const standardIndex = Math.round(currentAngle / anglePerStandardImage) % standardImageCount;
  
  return standardIndex;
};

// Generate floor plan WebP image URLs for an apartment based on actual S3 files
export const generateFloorPlanImages = (apartmentId: number): string[] => {
  const cloudfrontUrl = 'https://d2tbav89ixiad0.cloudfront.net';
  const images: string[] = [];
  
  // Map numeric apartment ID to apartment code (A1, A2, B1, etc.)
  let apartmentCode = '';
  if (apartmentId <= 11) {
    apartmentCode = `A${apartmentId}`;
  } else {
    apartmentCode = `B${apartmentId - 11}`;
  }
  
  // Based on the actual file list from S3 bucket, generate all 44 floor plan images
  // and filter for the specific apartment
  const allFloorPlanFiles = [
    '1A1_ergebnis.webp', '10A3_ergebnis.webp', '11B3_ergebnis.webp', '12B3_ergebnis.webp',
    '13A4_ergebnis.webp', '14A4_ergebnis.webp', '15B4_ergebnis.webp', '16B4_ergebnis.webp',
    '17A5_ergebnis.webp', '18A5_ergebnis.webp', '19B5_ergebnis.webp', '20B5_ergebnis.webp',
    '21A6_ergebnis.webp', '22A6_ergebnis.webp', '23B6_ergebnis.webp', '24B6_ergebnis.webp',
    '25A7_ergebnis.webp', '26A7_ergebnis.webp', '27B7_ergebnis.webp', '28B7_ergebnis.webp',
    '29A8_ergebnis.webp', '30A8_ergebnis.webp', '31B8_ergebnis.webp', '32B8_ergebnis.webp',
    '33A9_ergebnis.webp', '34A9_ergebnis.webp', '35B9_ergebnis.webp', '36B9_ergebnis.webp',
    '37A10_ergebnis.webp', '38A10_ergebnis.webp', '39B10_ergebnis.webp', '40B10_ergebnis.webp',
    '41A11_ergebnis.webp', '42A11_ergebnis.webp', '43B11_ergebnis.webp', '44B11_ergebnis.webp',
    '2A1_ergebnis.webp', '5A2_ergebnis.webp', '6A2_ergebnis.webp', '5B1_ergebnis.webp',
    '6B1_ergebnis.webp', '7B2_ergebnis.webp', '8B2_ergebnis.webp', '9A3_ergebnis.webp'
  ];
  
  // Filter files that match the current apartment code
  const matchingFiles = allFloorPlanFiles.filter(filename => 
    filename.includes(apartmentCode + '_ergebnis.webp')
  );
  
  // Sort by the leading number (lower numbers first)
  matchingFiles.sort((a, b) => {
    const aNum = parseInt(a.match(/^(\d+)/)?.[1] || '0');
    const bNum = parseInt(b.match(/^(\d+)/)?.[1] || '0');
    return aNum - bNum;
  });
  
  // Generate full CloudFront URLs
  matchingFiles.forEach(filename => {
    images.push(`${cloudfrontUrl}/Grundrisse_webp/${filename}`);
  });
  
  return images;
};

// Transform TypeScript Apartment to Firestore German fields
const apartmentToFirestore = (apartment: Apartment) => {
  const { id, price, size, rooms, floor, status, available, ...rest } = apartment;
  
  // Use new status field, fallback to available for backward compatibility
  const apartmentStatus = status || (available ? 'available' : 'reserved');
  
  return {
    ...rest,
    Preis: price,
    Grösse: size, 
    Zimmer: rooms,
    Etage: floor === 0 ? 'EG' : floor === 3 ? 'Attika' : `${floor}. OG`,
    Status: apartmentStatus,
    // Keep Verfügbar for backward compatibility
    Verfügbar: apartmentStatus === 'available'
  };
};

// Transform Firestore German fields to TypeScript Apartment
const firestoreToApartment = (firestoreData: any, docId: string): Apartment => {
  const { Preis, Grösse, Zimmer, Etage, Verfügbar, Status, Name, name, ...rest } = firestoreData;
  
  // Convert Etage string back to number
  let floor = 0;
  if (Etage === 'EG') floor = 0;
  else if (Etage === 'Attika') floor = 3;
  else if (typeof Etage === 'string' && Etage.includes('.')) {
    floor = parseInt(Etage.split('.')[0]);
  }
  
  // Extract numeric ID from document ID (A1 -> 1, B1 -> 12, etc.)
  let numericId = 1;
  if (docId.startsWith('A')) {
    numericId = parseInt(docId.substring(1));
  } else if (docId.startsWith('B')) {
    numericId = parseInt(docId.substring(1)) + 11;
  }

  // Generate dummy images based on apartment ID
  const cloudfrontUrl = 'https://d2tbav89ixiad0.cloudfront.net';
  
  const getFloorPlanFilename = (id: number): string => {
    const mapping: { [key: number]: string } = {
      1: 'Wohnung A1.1',
      2: 'Wohnung A1.3',
      3: 'Wohnung A1.2',
      4: 'Wohnung A2.1',
      5: 'Wohnung A2.3',
      6: 'Wohnung A2.2',
      7: 'Wohnung A3.1',
      8: 'Wohnung A3.3',
      9: 'Wohnung A3.2',
      10: 'Wohnung A4.1',
      11: 'Wohnung A4.2',
      12: 'Wohnung B1.1',
      13: 'Wohnung B1.3',
      14: 'Wohnung B1.2',
      15: 'Wohnung B2.1',
      16: 'Wohnung B2.3',
      17: 'Wohnung B2.2',
      18: 'Wohnung B3.1',
      19: 'Wohnung B3.3',
      20: 'Wohnung B3.2',
      21: 'Wohnung B4.1',
      22: 'Wohnung B4.2'
    };
    return `${mapping[id] || `Wohnung A${id}`}.pdf`;
  };

  // Determine status with backward compatibility
  let apartmentStatus: ApartmentStatus = 'available';
  if (Status) {
    apartmentStatus = Status as ApartmentStatus;
  } else if (Verfügbar === false) {
    apartmentStatus = 'reserved';
  }

  return {
    id: numericId,
    name: Name || name || `Wohnung ${docId}`, // Use new Name field first, then fallback
    address: rest.address || "Zürichstrasse 50, 8340 Hinwil",
    price: Preis || 0,
    size: Grösse || 0,
    rooms: Zimmer || 0,
    floor: floor,
    status: apartmentStatus,
    // Keep available for backward compatibility
    available: apartmentStatus === 'available',
    description: rest.description || "",
    features: Array.isArray(rest.features) ? rest.features : [],
    images360: generateBuilding360Images(), // Use dummy 360° images
    thumbnail: getProfileImage(numericId), // Use new profile images
    floorPlan: `${cloudfrontUrl}/PDF-Grundrisse/${getFloorPlanFilename(numericId)}`, // Use new floor plan naming
    floorPlanImages: generateFloorPlanImages(numericId), // Generate floor plan WebP images
    constructionYear: rest.constructionYear || "Frühjahr 2027",
    heatingType: rest.heatingType || "Fernwärme",
    parking: rest.parking || false,
    balcony: rest.balcony || false,
    elevator: rest.elevator || true,
    firestoreId: docId
  } as Apartment;
};

// Convert numeric apartment ID to Firestore document ID
const getDocumentId = (apartmentId: number): string => {
  if (apartmentId <= 11) {
    return `A${apartmentId}`;
  } else {
    return `B${apartmentId - 11}`;
  }
};

// Apartment operations
export class ApartmentService {
  private static collection = collection(db, COLLECTIONS.APARTMENTS);

  /**
   * Get all apartments
   */
  static async getAllApartments(): Promise<Apartment[]> {
    try {
      const querySnapshot = await getDocs(this.collection);
      const apartments: Apartment[] = [];
      
      querySnapshot.docs.forEach(doc => {
        try {
          const apartment = firestoreToApartment(doc.data(), doc.id);
          apartments.push(apartment);
        } catch (error) {
          console.warn(`Error processing apartment ${doc.id}:`, error);
        }
      });
      
      // Sort by numeric ID
      return apartments.sort((a, b) => a.id - b.id);
    } catch (error) {
      console.error('Error getting apartments:', error);
      throw error;
    }
  }

  /**
   * Get apartment by ID
   */
  static async getApartmentById(apartmentId: number): Promise<Apartment | null> {
    try {
      const docId = getDocumentId(apartmentId);
      const docRef = doc(db, COLLECTIONS.APARTMENTS, docId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return firestoreToApartment(docSnap.data(), docSnap.id);
      }
      return null;
    } catch (error) {
      console.error('Error getting apartment:', error);
      throw error;
    }
  }

  /**
   * Update apartment - only core fields (Preis, Grösse, Zimmer, Etage, Verfügbar)
   */
  static async updateApartment(apartmentId: number, updates: Partial<Apartment>): Promise<void> {
    try {
      const docId = getDocumentId(apartmentId);
      const docRef = doc(db, COLLECTIONS.APARTMENTS, docId);
      
      // Only allow updates to core fields
      const allowedUpdates: any = {};
      
      if (updates.name !== undefined) allowedUpdates.Name = updates.name;
      if (updates.price !== undefined) allowedUpdates.Preis = updates.price;
      if (updates.size !== undefined) allowedUpdates.Grösse = updates.size;
      if (updates.rooms !== undefined) allowedUpdates.Zimmer = updates.rooms;
      if (updates.available !== undefined) allowedUpdates.Verfügbar = updates.available;
      if (updates.status !== undefined) allowedUpdates.Status = updates.status; // Add status field support
      if (updates.floor !== undefined) {
        allowedUpdates.Etage = updates.floor === 0 ? 'EG' : 
                              updates.floor === 3 ? 'Attika' : 
                              `${updates.floor}. OG`;
      }
      
      await updateDoc(docRef, allowedUpdates);
    } catch (error) {
      console.error('Error updating apartment:', error);
      throw error;
    }
  }

  /**
   * Update apartment availability only
   */
  static async updateAvailability(apartmentId: number, available: boolean): Promise<void> {
    try {
      const docId = getDocumentId(apartmentId);
      const docRef = doc(db, COLLECTIONS.APARTMENTS, docId);
      await updateDoc(docRef, { Verfügbar: available });
    } catch (error) {
      console.error('Error updating apartment availability:', error);
      throw error;
    }
  }

  /**
   * Get available apartments
   */
  static async getAvailableApartments(): Promise<Apartment[]> {
    try {
      const q = query(
        this.collection, 
        where('Verfügbar', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const apartments: Apartment[] = [];
      
      querySnapshot.docs.forEach(doc => {
        try {
          const apartment = firestoreToApartment(doc.data(), doc.id);
          apartments.push(apartment);
        } catch (error) {
          console.warn(`Error processing apartment ${doc.id}:`, error);
        }
      });
      
      return apartments.sort((a, b) => a.price - b.price);
    } catch (error) {
      console.error('Error getting available apartments:', error);
      throw error;
    }
  }

  /**
   * Get apartments by price range
   */
  static async getApartmentsByPriceRange(minPrice: number, maxPrice: number): Promise<Apartment[]> {
    try {
      const q = query(
        this.collection,
        where('Preis', '>=', minPrice),
        where('Preis', '<=', maxPrice)
      );
      const querySnapshot = await getDocs(q);
      const apartments: Apartment[] = [];
      
      querySnapshot.docs.forEach(doc => {
        try {
          const apartment = firestoreToApartment(doc.data(), doc.id);
          apartments.push(apartment);
        } catch (error) {
          console.warn(`Error processing apartment ${doc.id}:`, error);
        }
      });
      
      return apartments.sort((a, b) => a.price - b.price);
    } catch (error) {
      console.error('Error getting apartments by price range:', error);
      throw error;
    }
  }

  /**
   * Get apartments by room count
   */
  static async getApartmentsByRooms(rooms: number): Promise<Apartment[]> {
    try {
      const q = query(
        this.collection,
        where('Zimmer', '==', rooms)
      );
      const querySnapshot = await getDocs(q);
      const apartments: Apartment[] = [];
      
      querySnapshot.docs.forEach(doc => {
        try {
          const apartment = firestoreToApartment(doc.data(), doc.id);
          apartments.push(apartment);
        } catch (error) {
          console.warn(`Error processing apartment ${doc.id}:`, error);
        }
      });
      
      return apartments.sort((a, b) => a.price - b.price);
    } catch (error) {
      console.error('Error getting apartments by rooms:', error);
      throw error;
    }
  }

  // Legacy methods for compatibility (not recommended for existing data)
  static async addApartment(apartment: Omit<Apartment, 'id'>): Promise<string> {
    throw new Error('Adding new apartments not supported with existing structure');
  }

  static async deleteApartment(apartmentId: number): Promise<void> {
    throw new Error('Deleting apartments not supported with existing structure');
  }
}

// Settings operations (unchanged)
export class SettingsService {
  private static collection = collection(db, COLLECTIONS.SETTINGS);

  /**
   * Get app settings
   */
  static async getSettings(): Promise<any> {
    try {
      const querySnapshot = await getDocs(this.collection);
      const settings: any = {};
      querySnapshot.docs.forEach(doc => {
        settings[doc.id] = doc.data();
      });
      return settings;
    } catch (error) {
      console.error('Error getting settings:', error);
      throw error;
    }
  }

  /**
   * Update setting
   */
  static async updateSetting(key: string, value: any): Promise<void> {
    try {
      const docRef = doc(db, COLLECTIONS.SETTINGS, key);
      await updateDoc(docRef, { value, updatedAt: new Date() });
    } catch (error) {
      console.error('Error updating setting:', error);
      throw error;
    }
  }
}

// Analytics tracking
export const trackEvent = (eventName: string, parameters?: any) => {
  console.log('Analytics event:', eventName, parameters);
};

export default { ApartmentService, SettingsService, trackEvent }; 